import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, notification } from "antd";
import { useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  IoArrowBackOutline,
  IoAppsOutline,
  IoCameraOutline,
  IoChatboxEllipsesOutline,
  IoCogOutline,
  IoCubeOutline,
  IoDocumentTextOutline,
  IoGlobeOutline,
  IoHeadsetOutline,
  IoInformationCircleOutline,
  IoMailOutline,
  IoMicOutline,
  IoPaperPlaneOutline,
  IoRefreshOutline,
  IoVolumeHighOutline,
} from "react-icons/io5";
import { useAuth } from "../../services/AuthContext";
import { LANGUAGE_OPTIONS, getLanguageCopy, normalizeLanguage } from "../../services/language";
import { setLanguage as setI18nLanguage, t as i18nT, t as tKey } from "../../i18n";
import {
  DEFAULT_BLOCK_ANIMATION_SPEED,
  DEFAULT_PREFERENCES,
  MAX_BLOCK_ANIMATION_SPEED,
  MIN_BLOCK_ANIMATION_SPEED,
  getPreferencesStorageKey,
  normalizeBlockAnimationSpeed,
  parsePreferences,
  writeGlobalBlockAnimationSpeed,
  writeUserPreferences,
} from "../../services/userSettings";
import {
  VOICE_CONTROL_STATUS,
  createVoiceControlController,
  getVoiceControlStatusText,
  getVoiceRecognitionLanguage,
} from "../../services/voiceControl";

const SECTIONS = ["general", "language", "support"];
const SUPPORT_EMAIL = "info@juegoxima.com";

const SETTINGS_COPY = {
  sectionDescription: {
    es: "Configura idioma, soporte y preferencias del juego.",
    en: "Configure language, support, and game preferences.",
    ru: "Настройте язык, поддержку и параметры игры.",
    de: "Sprache, Support und Spieleinstellungen konfigurieren.",
  },
  generalTitle: {
    es: "Preferencias generales",
    en: "General preferences",
    ru: "Общие настройки",
    de: "Allgemeine Einstellungen",
  },
  generalDescription: {
    es: "Opciones para personalizar tu experiencia.",
    en: "Options to personalize your experience.",
    ru: "Параметры для персонализации вашего опыта.",
    de: "Optionen zur Personalisierung Ihrer Erfahrung.",
  },
  gameSound: { es: "Sonido del juego", en: "Game sound", ru: "Звук игры", de: "Spielsound" },
  activated: { es: "Activado", en: "Activated", ru: "Активировано", de: "Aktiviert" },
  deactivated: { es: "Desactivado", en: "Deactivated", ru: "Отключено", de: "Deaktiviert" },
  animations: { es: "Animaciones", en: "Animations", ru: "Анимации", de: "Animationen" },
  activatedPlural: { es: "Activadas", en: "Activated", ru: "Включены", de: "Aktiviert" },
  deactivatedPlural: { es: "Desactivadas", en: "Deactivated", ru: "Выключены", de: "Deaktiviert" },
  autoEnterLastMode: {
    es: "Auto entrar al ultimo modo",
    en: "Auto enter last mode",
    ru: "Автоматический вход в последний режим",
    de: "Automatisch letzten Modus öffnen",
  },
  compactRightPanel: {
    es: "Panel derecho compacto",
    en: "Compact right panel",
    ru: "Компактная правая панель",
    de: "Kompakte rechte Seite",
  },
  cameraFollow: {
    es: "Activar seguimiento de cámara",
    en: "Enable camera follow",
    ru: "Включить слежение камеры",
    de: "Kameraverfolgung aktivieren",
  },
  voiceControl: {
    es: "Control de voz",
    en: "Voice control",
    ru: "Voice control",
    de: "Sprachsteuerung",
  },
  voiceControlDescription: {
    es: "Permite controlar movimientos, camara y rotacion del tablero mediante comandos de voz.",
    en: "Control movement, camera, and board rotation with voice commands.",
    ru: "Control movement, camera, and board rotation with voice commands.",
    de: "Steuert Bewegung, Kamera und Brettrotation per Sprachbefehl.",
  },
  voiceControlPrivacy: {
    es: "No se guarda audio ni historial permanente de transcripciones.",
    en: "Audio and permanent transcription history are not stored.",
    ru: "Audio and permanent transcription history are not stored.",
    de: "Audio und dauerhafte Transkriptionsverlaeufe werden nicht gespeichert.",
  },
  voicePermissionDeniedDescription: {
    es: "No se pudo activar el control de voz porque el permiso del microfono fue rechazado.",
    en: "Voice control could not be enabled because microphone permission was denied.",
    ru: "Voice control could not be enabled because microphone permission was denied.",
    de: "Die Sprachsteuerung konnte nicht aktiviert werden, weil die Mikrofonberechtigung abgelehnt wurde.",
  },
  voicePermissionConfirmTitle: {
    es: "Activar control de voz",
    en: "Enable voice control",
    ru: "Enable voice control",
    de: "Sprachsteuerung aktivieren",
  },
  voicePermissionConfirmDescription: {
    es: "XIMA usara el microfono para escuchar comandos de juego. No guardamos audio.",
    en: "XIMA will use the microphone to listen for game commands. Audio is not stored.",
    ru: "XIMA will use the microphone to listen for game commands. Audio is not stored.",
    de: "XIMA nutzt das Mikrofon fuer Spielbefehle. Audio wird nicht gespeichert.",
  },
  voicePermissionConfirmOk: {
    es: "Activar microfono",
    en: "Enable microphone",
    ru: "Enable microphone",
    de: "Mikrofon aktivieren",
  },
  voicePermissionConfirmCancel: {
    es: "Ahora no",
    en: "Not now",
    ru: "Not now",
    de: "Jetzt nicht",
  },
  voicePermissionCancelledDescription: {
    es: "No se activo el microfono.",
    en: "The microphone was not enabled.",
    ru: "The microphone was not enabled.",
    de: "Das Mikrofon wurde nicht aktiviert.",
  },
  voiceUnavailableDescription: {
    es: "El control de voz no esta disponible en este dispositivo.",
    en: "Voice control is not available on this device.",
    ru: "Voice control is not available on this device.",
    de: "Die Sprachsteuerung ist auf diesem Geraet nicht verfuegbar.",
  },
  blockAnimationSpeed: {
    es: "Velocidad de animaciones",
    en: "Animation speed",
    ru: "Скорость анимации",
    de: "Animationsgeschwindigkeit",
  },
  blockAnimationSlow: { es: "Lenta", en: "Slow", ru: "Медленно", de: "Langsam" },
  blockAnimationNormal: { es: "Normal", en: "Normal", ru: "Нормально", de: "Normal" },
  blockAnimationFast: { es: "Rápida", en: "Fast", ru: "Быстро", de: "Schnell" },
  yes: { es: "Si", en: "Yes", ru: "Да", de: "Ja" },
  no: { es: "No", en: "No", ru: "Нет", de: "Nein" },
  reset: { es: "Restablecer", en: "Reset", ru: "Сбросить", de: "Zurücksetzen" },
  supportDescription: {
    es: "Reporta errores o solicita ayuda del equipo.",
    en: "Report errors or request help from the team.",
    ru: "Сообщите об ошибках или запросите помощь команды.",
    de: "Fehler melden oder Hilfe vom Team anfordern.",
  },
  supportEmail: {
    es: "Email de soporte",
    en: "Support email",
    ru: "Электронная почта поддержки",
    de: "Support-E-Mail",
  },
  subject: { es: "Asunto", en: "Subject", ru: "Тема", de: "Betreff" },
  subjectPlaceholder: {
    es: "Escribe el asunto de tu consulta...",
    en: "Write the subject of your request...",
    ru: "Напишите тему вашего обращения...",
    de: "Schreibe den Betreff deiner Anfrage...",
  },
  describeProblem: {
    es: "Describe tu problema",
    en: "Describe your problem",
    ru: "Опишите вашу проблему",
    de: "Beschreibe dein Problem",
  },
  describeProblemPlaceholder: {
    es: "Cuéntanos con detalle para poder ayudarte...",
    en: "Tell us the details so we can help you...",
    ru: "Расскажите подробнее, чтобы мы могли вам помочь...",
    de: "Erzähl uns die Details, damit wir dir helfen können...",
  },
};

const SETTINGS_LANGUAGE_VISUALS = {
  es: {
    title: "ESPAÑOL",
    description: "Interfaz en español",
  },
  en: {
    title: "ENGLISH",
    description: "Interface in English",
  },
  ru: {
    title: "РУССКИЙ",
    description: "Интерфейс на русском",
  },
  de: {
    title: "DEUTSCH",
    description: "Interface auf Deutsch",
  },
};


const Settings = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { user } = useAuth();
  const outletContext = useOutletContext() || {};

  const language = normalizeLanguage(outletContext.language);
  const setLanguage =
    typeof outletContext.setLanguage === "function" ? outletContext.setLanguage : () => {};
  useEffect(() => {
    setI18nLanguage(language);
  }, [language]);
  const ui = useCallback(
    (key) => SETTINGS_COPY[key]?.[language] || SETTINGS_COPY[key]?.en || "",
    [language]
  );

  const fromLobbySource = String(
    searchParams.get("fromLobbySource") || location.state?.fromLobbySource || ""
  ).toLowerCase();
  const settingsReturnRoute = fromLobbySource === "settings" ? "/game/lobby" : "/game";
  const activeSectionFromUrl = String(searchParams.get("section") || "general").toLowerCase();
  const activeSection = SECTIONS.includes(activeSectionFromUrl)
    ? activeSectionFromUrl
    : "general";

  const preferencesStorageKey = useMemo(
    () => getPreferencesStorageKey(user?.email),
    [user?.email]
  );

  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [supportSubject, setSupportSubject] = useState("");
  const [supportMessage, setSupportMessage] = useState("");
  const [supportSending, setSupportSending] = useState(false);
  const [voiceControlStatus, setVoiceControlStatus] = useState(
    DEFAULT_PREFERENCES.voiceControlEnabled
      ? VOICE_CONTROL_STATUS.AWAITING_PERMISSION
      : VOICE_CONTROL_STATUS.DISABLED
  );
  const [voiceControlBusy, setVoiceControlBusy] = useState(false);
  const voiceControlRef = useRef(null);
  const voiceControlStatusRef = useRef(voiceControlStatus);

  const ensureVoiceControl = useCallback(() => {
    if (!voiceControlRef.current) {
      voiceControlRef.current = createVoiceControlController({
        language: getVoiceRecognitionLanguage(language),
        onStatusChange: setVoiceControlStatus,
        shouldProcessCommand: () => false,
      });
    } else {
      voiceControlRef.current.updateOptions({
        language: getVoiceRecognitionLanguage(language),
        onStatusChange: setVoiceControlStatus,
        shouldProcessCommand: () => false,
      });
    }
    return voiceControlRef.current;
  }, [language]);

  useEffect(() => {
    const raw = localStorage.getItem(preferencesStorageKey);
    const parsed = parsePreferences(raw);
    setPreferences(parsed || DEFAULT_PREFERENCES);
  }, [preferencesStorageKey]);

  useEffect(
    () => () => {
      voiceControlRef.current?.destroy();
      voiceControlRef.current = null;
    },
    []
  );

  useEffect(() => {
    voiceControlStatusRef.current = voiceControlStatus;
  }, [voiceControlStatus]);

  useEffect(() => {
    if (!preferences.voiceControlEnabled) {
      const currentVoiceControlStatus = voiceControlStatusRef.current;
      const shouldKeepFailureStatus = [
        VOICE_CONTROL_STATUS.PERMISSION_DENIED,
        VOICE_CONTROL_STATUS.UNAVAILABLE,
        VOICE_CONTROL_STATUS.UNSUPPORTED,
        VOICE_CONTROL_STATUS.ERROR,
      ].includes(currentVoiceControlStatus);
      voiceControlRef.current?.stop();
      setVoiceControlStatus(
        shouldKeepFailureStatus ? currentVoiceControlStatus : VOICE_CONTROL_STATUS.DISABLED
      );
      return undefined;
    }

    const controller = ensureVoiceControl();
    let cancelled = false;
    controller.start({ requestPermission: false }).then(() => {
      if (cancelled) controller.stop();
    });

    return () => {
      cancelled = true;
    };
  }, [ensureVoiceControl, preferences.voiceControlEnabled]);

  const setSection = (section) => {
    if (!SECTIONS.includes(section)) return;
    setSearchParams({ section });
  };

  const persistPreferences = (nextPreferences) => {
    writeUserPreferences(user?.email, nextPreferences);
  };

  // Cada cambio se guarda al momento; ya no hay botón de guardar.
  const togglePreference = (key) => {
    setPreferences((prev) => {
      const nextPreferences = {
        ...prev,
        [key]: !prev[key],
      };
      persistPreferences(nextPreferences);
      return nextPreferences;
    });
  };

  const setBlockAnimationSpeedPreference = (rawValue) => {
    const nextValue = normalizeBlockAnimationSpeed(rawValue);
    setPreferences((prev) => {
      const nextPreferences = {
        ...prev,
        blockAnimationSpeed: nextValue,
      };
      persistPreferences(nextPreferences);
      return nextPreferences;
    });
  };

  const setVoicePreference = (enabled) => {
    setPreferences((prev) => {
      const nextPreferences = {
        ...prev,
        voiceControlEnabled: enabled === true,
      };
      persistPreferences(nextPreferences);
      return nextPreferences;
    });
  };

  const confirmVoiceMicrophonePermission = useCallback(
    () =>
      new Promise((resolve) => {
        Modal.confirm({
          title: ui("voicePermissionConfirmTitle"),
          content: ui("voicePermissionConfirmDescription"),
          okText: ui("voicePermissionConfirmOk"),
          cancelText: ui("voicePermissionConfirmCancel"),
          centered: true,
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      }),
    [ui]
  );

  const toggleVoiceControl = async () => {
    if (voiceControlBusy) return;

    if (preferences.voiceControlEnabled) {
      voiceControlRef.current?.stop();
      setVoicePreference(false);
      setVoiceControlStatus(VOICE_CONTROL_STATUS.DISABLED);
      return;
    }

    const confirmed = await confirmVoiceMicrophonePermission();
    if (!confirmed) {
      setVoicePreference(false);
      setVoiceControlStatus(VOICE_CONTROL_STATUS.DISABLED);
      notification.info({
        message: ui("voiceControl"),
        description: ui("voicePermissionCancelledDescription"),
      });
      return;
    }

    setVoiceControlBusy(true);
    const controller = ensureVoiceControl();
    const started = await controller.start({ requestPermission: true });
    setVoiceControlBusy(false);

    if (!started) {
      setVoicePreference(false);
      const denied =
        controller.status === VOICE_CONTROL_STATUS.PERMISSION_DENIED ||
        voiceControlStatus === VOICE_CONTROL_STATUS.PERMISSION_DENIED;
      notification.error({
        message: ui("voiceControl"),
        description: denied
          ? ui("voicePermissionDeniedDescription")
          : ui("voiceUnavailableDescription"),
      });
      return;
    }

    setVoicePreference(true);
  };

  const currentBlockAnimationSpeed = normalizeBlockAnimationSpeed(
    preferences.blockAnimationSpeed ?? DEFAULT_BLOCK_ANIMATION_SPEED
  );
  const blockAnimationSpeedStatus =
    currentBlockAnimationSpeed <= 0.75
      ? ui("blockAnimationSlow")
      : currentBlockAnimationSpeed >= 1.25
        ? ui("blockAnimationFast")
        : ui("blockAnimationNormal");
  const blockAnimationSpeedPercent = Math.round(
    ((currentBlockAnimationSpeed - MIN_BLOCK_ANIMATION_SPEED) /
      Math.max(0.01, MAX_BLOCK_ANIMATION_SPEED - MIN_BLOCK_ANIMATION_SPEED)) *
      100
  );

  const resetPreferences = () => {
    localStorage.removeItem(preferencesStorageKey);
    writeGlobalBlockAnimationSpeed(DEFAULT_PREFERENCES.blockAnimationSpeed);
    setPreferences(DEFAULT_PREFERENCES);
    setVoiceControlStatus(
      DEFAULT_PREFERENCES.voiceControlEnabled
        ? VOICE_CONTROL_STATUS.AWAITING_PERMISSION
        : VOICE_CONTROL_STATUS.DISABLED
    );
    notification.info({
      message: i18nT("settings.ajustesRestablecidos"),
      description: i18nT("settings.seRestauraronLosValoresPorDefecto"),
    });
  };

  const buildSupportMailtoUrl = (subject, message) => {
    const fromEmail = String(user?.email || "").trim();
    const preamble = fromEmail ? `Usuario: ${fromEmail}\n\n` : "";
    const body = `${preamble}${message}`;
    return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const sendSupportEmail = async () => {
    const subject = supportSubject.trim();
    const message = supportMessage.trim();

    if (!subject || !message) {
      notification.warning({
        message: i18nT("settings.completaElFormulario"),
        description: i18nT("settings.debesEscribirAsuntoYDescripcionAntesDeEnviar"),
      });
      return;
    }

    setSupportSending(true);
    try {
      await axios.post("/support/email", {
        subject,
        message,
      });

      notification.success({
        message: i18nT("settings.mensajeEnviado"),
        description: `Tu solicitud fue enviada a ${SUPPORT_EMAIL}.`,
      });
      setSupportSubject("");
      setSupportMessage("");
    } catch (error) {
      const apiMessage = String(error?.response?.data?.message || "").trim();
      const canUseFallbackMailClient = typeof window !== "undefined";

      if (canUseFallbackMailClient) {
        window.location.href = buildSupportMailtoUrl(subject, message);
      }

      notification.error({
        message: i18nT("settings.noSePudoEnviarAutomaticamente"),
        description:
          apiMessage ||
          `Abrimos tu app de correo para completar el envio a ${SUPPORT_EMAIL}.`,
      });
    } finally {
      setSupportSending(false);
    }
  };

  const voiceStatusText = getVoiceControlStatusText(voiceControlStatus, language);

  return (
    <main className="xima-settings-screen">
      <div className="xima-settings-shell">
        <header className="xima-settings-header">
          <div className="xima-settings-title-wrap">
            <span className="xima-settings-title-icon" aria-hidden="true">
              <IoCogOutline />
            </span>
            <div>
              <h1>{tKey("AJUSTES")}</h1>
              <p>{ui("sectionDescription")}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate(settingsReturnRoute)}
            className="xima-settings-back"
          >
            <IoArrowBackOutline aria-hidden="true" />
            <span>{tKey("VOLVER_AL_LOBBY")}</span>
          </button>
        </header>

        <div className="xima-settings-body">
          <aside className="xima-settings-sidebar">
            <div className="xima-settings-side-list">
              <button
                type="button"
                onClick={() => setSection("general")}
                className={`xima-settings-side-button ${
                  activeSection === "general" ? "xima-settings-side-active" : ""
                }`}
              >
                <IoCogOutline aria-hidden="true" />
                <span>{tKey("GENERAL")}</span>
              </button>

              <button
                type="button"
                onClick={() => setSection("language")}
                className={`xima-settings-side-button ${
                  activeSection === "language" ? "xima-settings-side-active" : ""
                }`}
              >
                <IoGlobeOutline aria-hidden="true" />
                <span>{tKey("IDIOMA")}</span>
              </button>

              <button
                type="button"
                onClick={() => setSection("support")}
                className={`xima-settings-side-button ${
                  activeSection === "support" ? "xima-settings-side-active" : ""
                }`}
              >
                <IoHeadsetOutline aria-hidden="true" />
                <span>{tKey("SOPORTE")}</span>
              </button>
            </div>
          </aside>

          <section className="xima-settings-panel">
            {activeSection === "general" ? (
              <div className="xima-general-section">
                <div className="xima-settings-section-head">
                  <h2>{ui("generalTitle")}</h2>
                  <p>{ui("generalDescription")}</p>
                </div>

                <div className="xima-general-list">
                  <button
                    type="button"
                    onClick={() => togglePreference("soundEnabled")}
                    className={`xima-general-row ${
                      preferences.soundEnabled ? "xima-general-row-on" : "xima-general-row-off"
                    }`}
                  >
                    <span className="xima-general-icon xima-general-icon-sound">
                      <IoVolumeHighOutline aria-hidden="true" />
                    </span>
                    <span className="xima-general-copy">
                      <strong>{ui("gameSound")}</strong>
                      <small>{i18nT("settings.activaODesactivaTodosLosSonidosDelJuego")}</small>
                    </span>
                    <span className="xima-general-status">
                      {preferences.soundEnabled ? ui("activated") : ui("deactivated")}
                    </span>
                    <span className="xima-general-toggle" aria-hidden="true" />
                  </button>

                  <button
                    type="button"
                    onClick={() => togglePreference("animationsEnabled")}
                    className={`xima-general-row ${
                      preferences.animationsEnabled ? "xima-general-row-on" : "xima-general-row-off"
                    }`}
                  >
                    <span className="xima-general-icon xima-general-icon-animation">
                      <IoCubeOutline aria-hidden="true" />
                    </span>
                    <span className="xima-general-copy">
                      <strong>{ui("animations")}</strong>
                      <small>{i18nT("settings.habilitaODeshabilitaLasAnimacionesDelJuego")}</small>
                    </span>
                    <span className="xima-general-status">
                      {preferences.animationsEnabled ? ui("activatedPlural") : ui("deactivatedPlural")}
                    </span>
                    <span className="xima-general-toggle" aria-hidden="true" />
                  </button>

                  <button
                    type="button"
                    onClick={() => togglePreference("cameraFollowEnabled")}
                    className={`xima-general-row ${
                      preferences.cameraFollowEnabled ? "xima-general-row-on" : "xima-general-row-off"
                    }`}
                  >
                    <span className="xima-general-icon xima-general-icon-camera">
                      <IoCameraOutline aria-hidden="true" />
                    </span>
                    <span className="xima-general-copy">
                      <strong>{ui("cameraFollow")}</strong>
                      <small>{i18nT("settings.laCamaraSeguiraAutomaticamenteLaAccion")}</small>
                    </span>
                    <span className="xima-general-status">
                      {preferences.cameraFollowEnabled ? ui("activated") : ui("deactivated")}
                    </span>
                    <span className="xima-general-toggle" aria-hidden="true" />
                  </button>

                  <div className="xima-general-row xima-general-speed-row">
                    <span className="xima-general-icon xima-general-icon-speed">
                      <IoAppsOutline aria-hidden="true" />
                    </span>
                    <span className="xima-general-copy">
                      <strong>{ui("blockAnimationSpeed")}</strong>
                      <small>{i18nT("settings.ajustaLaVelocidadDeLasAnimacionesDeLos")}</small>
                    </span>
                    <span className="xima-general-range-wrap">
                      <span className="xima-general-speed-status">{blockAnimationSpeedStatus}</span>
                      <input
                        type="range"
                        min={MIN_BLOCK_ANIMATION_SPEED}
                        max={MAX_BLOCK_ANIMATION_SPEED}
                        step="0.05"
                        value={currentBlockAnimationSpeed}
                        onChange={(event) =>
                          setBlockAnimationSpeedPreference(event.target.value)
                        }
                        aria-label={ui("blockAnimationSpeed")}
                        className="xima-general-range"
                        style={{
                          "--xima-general-range-progress": `${blockAnimationSpeedPercent}%`,
                        }}
                      />
                      <span className="xima-general-range-labels">
                        <span>{ui("blockAnimationSlow")}</span>
                        <span>{ui("blockAnimationNormal")}</span>
                        <span>{ui("blockAnimationFast")}</span>
                      </span>
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={toggleVoiceControl}
                    disabled={voiceControlBusy}
                    className={`xima-general-row xima-general-voice-row ${
                      preferences.voiceControlEnabled ? "xima-general-row-on" : "xima-general-row-off"
                    } ${voiceControlBusy ? "xima-general-row-busy" : ""}`}
                    aria-pressed={Boolean(preferences.voiceControlEnabled)}
                    aria-label={`${ui("voiceControl")}: ${voiceStatusText}`}
                  >
                    <span className="xima-general-icon xima-general-icon-voice">
                      <IoMicOutline aria-hidden="true" />
                    </span>
                    <span className="xima-general-copy">
                      <strong>{ui("voiceControl")}</strong>
                      <small>{ui("voiceControlDescription")}</small>
                      <small className="xima-general-voice-state">{voiceStatusText}</small>
                      <small>{ui("voiceControlPrivacy")}</small>
                    </span>
                    <span className="xima-general-status">
                      {preferences.voiceControlEnabled ? ui("activated") : ui("deactivated")}
                    </span>
                    <span className="xima-general-toggle" aria-hidden="true" />
                  </button>
                </div>

                <div className="xima-general-actions">
                  <button
                    type="button"
                    onClick={resetPreferences}
                    className="xima-general-reset"
                  >
                    <IoRefreshOutline aria-hidden="true" />
                    <span>{ui("reset")}</span>
                  </button>
                </div>

                <div className="xima-general-note">
                  <IoInformationCircleOutline aria-hidden="true" />
                  <span>
                    {i18nT("settings.estosAjustesSeAplicanSoloATuDispositivo")}
                    <br />
                    {i18nT("settings.puedesRestablecerLosValoresPredeterminadosEnCualquierMomento")}
                  </span>
                </div>
              </div>
            ) : null}

            {activeSection === "language" ? (
              <div className="xima-language-section">
                <div className="xima-settings-section-head">
                  <h2>{tKey("IDIOMA")}</h2>
                  <p>{tKey("SELECCIONA_EL_IDIOMA_DE_TODA_LA_INTERFAZ")}</p>
                </div>

                <div className="xima-language-list">
                  {LANGUAGE_OPTIONS.map((option) => {
                    const isActive = language === option.value;
                    const visual = SETTINGS_LANGUAGE_VISUALS[option.value] || {
                      title: option.nativeLabel,
                      description: getLanguageCopy(language, option.description),
                    };

                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setLanguage(option.value)}
                        className={`xima-language-option ${
                          isActive ? "xima-language-option-active" : ""
                        }`}
                        aria-pressed={isActive}
                      >
                        <span
                          className={`xima-language-flag xima-language-flag-${option.value}`}
                          aria-hidden="true"
                        />
                        <span className="xima-language-copy">
                          <strong>{visual.title}</strong>
                          <small>{visual.description}</small>
                        </span>
                        <span className="xima-language-code">{option.shortLabel}</span>
                        <span className="xima-language-radio" aria-hidden="true" />
                      </button>
                    );
                  })}
                </div>

                <div className="xima-language-note">
                  <IoGlobeOutline aria-hidden="true" />
                  <span>
                    {i18nT("settings.elIdiomaSeAplicaraAutomaticamenteATodaLaInterfaz")}
                    <br />
                    {i18nT("settings.noAfectaElIdiomaDeOtrosJugadores")}
                  </span>
                </div>
              </div>
            ) : null}

            {activeSection === "support" ? (
              <div className="xima-support-section">
                <div className="xima-settings-section-head">
                  <h2>{tKey("SOPORTE")}</h2>
                  <p>{ui("supportDescription")}</p>
                </div>

                <div className="xima-support-form">
                  <div className="xima-support-field xima-support-email-card">
                    <span className="xima-support-icon xima-support-icon-mail" aria-hidden="true">
                      <IoMailOutline />
                    </span>
                    <div className="xima-support-copy">
                      <label>{ui("supportEmail")}</label>
                      <strong>{SUPPORT_EMAIL}</strong>
                    </div>
                  </div>

                  <label className="xima-support-field xima-support-input-field">
                    <span className="xima-support-icon xima-support-icon-subject" aria-hidden="true">
                      <IoDocumentTextOutline />
                    </span>
                    <span className="xima-support-copy">
                      <span>{ui("subject")}</span>
                      <input
                        value={supportSubject}
                        onChange={(event) => setSupportSubject(event.target.value)}
                        placeholder={ui("subjectPlaceholder")}
                      />
                    </span>
                  </label>

                  <label className="xima-support-field xima-support-message-field">
                    <span className="xima-support-icon xima-support-icon-message" aria-hidden="true">
                      <IoChatboxEllipsesOutline />
                    </span>
                    <span className="xima-support-copy">
                      <span>{ui("describeProblem")}</span>
                      <textarea
                        value={supportMessage}
                        onChange={(event) => setSupportMessage(event.target.value)}
                        placeholder={ui("describeProblemPlaceholder")}
                        rows={6}
                      />
                    </span>
                  </label>

                  <button
                    type="button"
                    onClick={sendSupportEmail}
                    disabled={supportSending}
                    className="xima-support-submit"
                  >
                    <IoPaperPlaneOutline aria-hidden="true" />
                    <span>{supportSending ? "Enviando..." : tKey("ENVIAR")}</span>
                  </button>
                </div>
              </div>
            ) : null}
          </section>
        </div>
      </div>
    </main>
  );
};

export default Settings;
